import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import programService from '../../services/programService';
import { authService } from '../../services/authService';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import '../../styles/programs/ProgramsPage.css';

export function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await programService.getAllPrograms();
      setPrograms(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="programs-page">
        <div className="programs-loading">
          <div className="loading-spinner"></div>
          <p>Loading programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="programs-page">
        <div className="programs-error">
          <p>{error}</p>
          <button onClick={fetchPrograms} className="btn-retry">Try Again</button>
        </div>
      </div>
    );
  }

  const getPrimaryPrice = (program) => {
    const offers = Array.isArray(program?.program_offers) ? program.program_offers : [];
    if (offers.length > 0) {
      const prices = offers.map(o => Number(o.price_inr || 0)).filter(p => !isNaN(p));
      if (prices.length) return Math.min(...prices);
    }
    return Number(program?.priceINR ?? program?.price_inr ?? 0);
  };

  const getCheapestOffer = (program) => {
    const offers = Array.isArray(program?.program_offers) ? program.program_offers : [];
    if (!offers.length) return null;
    return offers.reduce((best, o) => {
      const price = Number(o.price_inr || 0);
      if (isNaN(price)) return best;
      if (!best) return o;
      const bestPrice = Number(best.price_inr || 0);
      return price < bestPrice ? o : best;
    }, null);
  };

  // Trigger Razorpay checkout flow
  const handleBuyNow = async (program, offer = null) => {
    console.log('[BuyNow] clicked for program_id:', program?.program_id, 'offer:', offer?.id);
    // Strict check: token must exist and be valid on backend
    const hasValidSession = await authService.ensureUserSession();
    if (!hasValidSession) {
      // Save intended program to resume checkout after login
      // Use both localStorage and sessionStorage for reliability during OAuth
      localStorage.setItem('resume_checkout_program_id', program.program_id);
      sessionStorage.setItem('resume_checkout_program_id', program.program_id);
      if (offer?.id) {
        localStorage.setItem('resume_checkout_offer_id', offer.id);
        sessionStorage.setItem('resume_checkout_offer_id', offer.id);
      }
      console.log('[BuyNow] saved program_id to storage:', program.program_id);
      window.dispatchEvent(new CustomEvent('OPEN_AUTH_MODAL', { detail: { tab: 'login' } }));
      return;
    }

    try {
      const selectedOffer = offer || getCheapestOffer(program);
      const orderData = await programService.createOrder(program.program_id, selectedOffer?.id || null);
      const orderId = orderData.order_id;
      // Backend sends amount_paise (already in paise) or amount in INR
      const amountPaise = orderData.amount_paise || orderData.amount * 100 || 0;
      const keyId = orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
      
      console.log('[Razorpay Checkout] order_id:', orderId, 'amount (paise):', amountPaise);

      if (!keyId) {
        alert('Razorpay key not configured. Please contact support.');
        return;
      }

      // Load Razorpay checkout script if not present
      if (typeof window.Razorpay === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
          document.body.appendChild(script);
        });
      }

      const options = {
        key: keyId,
        amount: amountPaise, // Already in paise from backend
        currency: 'INR',
        name: 'Get Fit with Daniella',
        description: program.name,
        order_id: orderId,
        handler: async (response) => {
          try {
            const verify = await programService.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              program.program_id,
              selectedOffer?.id || null
            );
            if (verify?.success) {
              alert('Payment successful! Access granted.');
              // Navigate to dashboard after successful payment
              window.location.href = '/my-programs';
            } else {
              alert('Payment verification failed. Please try again.');
            }
          } catch (e) {
            console.error('Payment verification error:', e);
            // Check if it's an authentication error
            if (e.response?.status === 401) {
              alert('Authentication error. Please log in again and try the purchase.');
            } else {
              alert('Payment verification error.');
            }
          }
        },
        prefill: {
          email: '',
        },
        theme: { color: '#6a3df0' },
      };

      const rzp = new window.Razorpay(options);
      
      // Handle payment failure/cancellation
      rzp.on('payment.failed', (response) => {
        console.log('[Razorpay] Payment failed/cancelled:', response.error);
        alert('Payment cancelled or failed. Please try again.');
      });
      
      rzp.open();
    } catch (err) {
      console.error('Buy flow error:', err);
      // Handle duplicate purchase error
      if (err?.response?.status === 400 && err?.response?.data?.message?.includes('already have active access')) {
        alert('You already have access to this program. Redirecting to your dashboard...');
        window.location.href = '/my-programs';
      } else {
        const errorMessage = err?.response?.data?.message || 'Unable to start payment flow.';
        alert(errorMessage);
      }
    }
  };

  const renderProgramCard = (program) => {
    return (
      <div key={program.program_id} className="program-card">
        {program.badge && (
          <div className="program-card-badge">{program.badge}</div>
        )}
        <div className="program-card-image" style={{ background: program.color }}>
          <img src={program.image} alt={program.name} />
          <div className="program-card-overlay">
            <span className="program-duration">{program.duration}</span>
          </div>
              <div className="program-price-on-image">
                <span className="price-currency">Rs. </span>
                <span className="price-amount">{getPrimaryPrice(program).toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="program-card-content">
              <h3 className="program-name">{program.name}</h3>
              <p className="program-tagline">{program.tagline}</p>
          <p className="program-description">{program.description}</p>
          
          <div className="program-details">
            <div className="program-detail">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>{program.timeCommitment}</span>
            </div>
          </div>

          <ul className="program-features">
            {program.features && program.features.slice(0, 3).map((feature, index) => (
              <li key={index} className="program-feature">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {feature}
              </li>
            ))}
          </ul>

            <div className="program-card-footer">
              <div className="program-buttons">
                <Link to={`/programs/${program.program_id.toLowerCase()}`} className="btn-program">
                  Learn More
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </Link>
                {Array.isArray(program.program_offers) && program.program_offers.length > 0 ? (
                  <Link to={`/programs/${program.program_id.toLowerCase()}`} className="btn-buy-now">
                    View Options
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                  </Link>
                ) : (
                  <button className="btn-buy-now" onClick={() => handleBuyNow(program)}>
                    Buy Now
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
  };

  return (
    <div className="programs-page">
      <HelmetProvider>
        <Helmet>
          <title>
            Fitness Programs - Get Fit with Daniella
          </title>

          <meta
            name="description"
            content="Explore fitness programs from Get Fit with Daniella designed to help you build strength, healthy habits, and sustainable results."
          />

          <link rel="canonical" href="https://getfitwithdaniella.com/programs/" />

          {/* Open Graph */}
          <meta
            property="og:title"
            content="Fitness Programs - Get Fit with Daniella"
          />

          <meta
            property="og:description"
            content="Explore fitness programs from Get Fit with Daniella designed to help you build strength, healthy habits, and sustainable results."
          />

          <meta
            property="og:url"
            content="https://getfitwithdaniella.com/programs/"
          />

          <meta property="og:type" content="website" />

          <meta
            property="og:site_name"
            content="Get Fit with Daniella"
          />

          <meta
            property="og:image"
            content="https://getfitwithdaniella.com/assets/og-image.jpg"
          />

          {/* Twitter / X */}
          <meta
            name="twitter:card"
            content="summary_large_image"
          />

          <meta
            name="twitter:title"
            content="Fitness Programs - Get Fit with Daniella"
          />

          <meta
            name="twitter:description"
            content="Explore fitness programs from Get Fit with Daniella designed to help you build strength, healthy habits, and sustainable results."
          />

          <meta
            name="twitter:image"
            content="https://www.getfitwithdaniella.com/assets/og-image.jpg"
          />
        </Helmet>
      </HelmetProvider>
      {/* Programs Grid */}
      <section className="programs-grid-section">
        <div className="programs-container">
          <h2 className="section-title">Our Programs</h2>
          <p className="section-subtitle">Select the program that fits your goals and lifestyle</p>
          
          <div className="programs-grid">
            {programs.map(renderProgramCard)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="programs-cta">
        <div className="programs-cta-content">
          <h2>Not Sure Which Program is Right for You?</h2>
          <p>Take our quick quiz to find your perfect match based on your goals, schedule, and fitness level.</p>
          <Link to="/#programs" className="btn-cta-primary">
            Take the Quiz
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}

